import React, { useEffect, useState } from "react";
import { errorMessage } from "../../services";
import { getPlans, updatePlan } from "../../services/admin";
import Loader, { Spinner } from "../../components/Loader";

// One card per plan. New prices apply to future payments only; existing websites keep their price.
const formOf = (plan) => ({
  name: plan.name,
  price: plan.price,
  gstPercent: plan.gstPercent || 0,
  trialDays: plan.trialDays,
  minQuantity: plan.minQuantity,
  active: plan.active,
  firstMonthOffer: Boolean(plan.firstMonthOffer),
  firstMonthPrice: plan.firstMonthPrice ?? 1,
});

function PlanCard({ plan, notify, onSaved }) {
  const [form, setForm] = useState(() => formOf(plan));
  // After a save the card shows the values the server stored
  useEffect(() => setForm(formOf(plan)), [plan]);
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { name: form.name, active: form.active };
      if (plan.isTrial) payload.trialDays = Number(form.trialDays);
      else {
        payload.price = Number(form.price);
        payload.gstPercent = Number(form.gstPercent) || 0;
      }
      if (plan.key === "franchise") payload.minQuantity = Number(form.minQuantity);
      if (plan.key === "monthly") {
        payload.firstMonthOffer = form.firstMonthOffer;
        payload.firstMonthPrice = Number(form.firstMonthPrice) || 0;
      }
      const { data } = await updatePlan(plan.key, payload);
      notify(`${data.plan.name} plan saved`);
      onSaved(data.plan);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };

  const unit = plan.key === "yearly" ? "per website, per year" : "per website, per month";
  return (
    <form className="plan-edit-card" onSubmit={save}>
      <h3>{plan.key.toUpperCase()}</h3>
      <span className="muted">{plan.isTrial ? "Free period for new websites" : unit}</span>
      <label className="admin-field">Display name<input value={form.name} onChange={set("name")} required /></label>
      {plan.isTrial ? (
        <label className="admin-field">Trial length (days)<input type="number" min="0" max="60" value={form.trialDays} onChange={set("trialDays")} /></label>
      ) : (
        <>
          <div className="form-row">
            <label className="admin-field">Price (₹)<input type="number" min="0" value={form.price} onChange={set("price")} /></label>
            <label className="admin-field">GST (%)<input type="number" min="0" max="28" step="0.01" value={form.gstPercent} onChange={set("gstPercent")} /></label>
          </div>
          <small className="field-help plan-gst-note">
            {Number(form.gstPercent) > 0
              ? `Customers pay ₹${Number(form.price || 0).toLocaleString("en-IN")} + ${form.gstPercent}% GST = ₹${(Number(form.price || 0) + Math.round((Number(form.price || 0) * Number(form.gstPercent)) / 100)).toLocaleString("en-IN")}`
              : "No GST is added (0%)."}
          </small>
        </>
      )}
      {plan.key === "franchise" && (
        <label className="admin-field">Minimum websites per order<input type="number" min="1" value={form.minQuantity} onChange={set("minQuantity")} /></label>
      )}
      {/* The first-month offer: new customers pay this for their first month, once per WhatsApp number and email */}
      {plan.key === "monthly" && (
        <div className="plan-offer">
          <div className="check-row"><label><input type="checkbox" checked={form.firstMonthOffer} onChange={set("firstMonthOffer")} />First-month offer for new customers</label></div>
          {form.firstMonthOffer && (
            <label className="admin-field">First month price (₹)<input type="number" min="0" value={form.firstMonthPrice} onChange={set("firstMonthPrice")} /></label>
          )}
          <small className="field-help">
            {form.firstMonthOffer
              ? `Plans show ₹${Number(form.firstMonthPrice || 0).toLocaleString("en-IN")} for the first month, then ₹${Number(form.price || 0).toLocaleString("en-IN")}/month. Each WhatsApp number and email gets it once.`
              : "Off: the plan shows its usual monthly price."}
          </small>
        </div>
      )}
      <div className="check-row"><label><input type="checkbox" checked={form.active} onChange={set("active")} />Available to customers</label></div>
      <button className="primary-btn" disabled={busy}>{busy ? <><Spinner /> Saving</> : "Save plan"}</button>
    </form>
  );
}

export default function PlansEditor({ notify }) {
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    getPlans()
      .then(({ data }) => setPlans(data.plans))
      .catch((err) => setError(errorMessage(err)));
  }, []);

  // The saved plan as the server returned it. The card keeps its own form, which already shows what was saved.
  const replacePlan = (saved) => setPlans((list) => list.map((p) => (p.key === saved.key ? { ...p, ...saved } : p)));

  return (
    <div className="panel info-panel">
      <h2>Plans & pricing</h2>
      <p>Changes apply to new payments and purchases only. GST is added on top of the price when a customer pays (0% = no GST).</p>
      {error && <div className="admin-error">{error}</div>}
      {!plans.length && !error && <Loader label="Loading plans" />}
      <div className="plan-edit-grid">
        {plans.map((plan) => <PlanCard key={plan.key} plan={plan} notify={notify} onSaved={replacePlan} />)}
      </div>
    </div>
  );
}
